import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Card, Row, Col, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CurrencyInput from './CurrencyInput';
import { closeCashRegister, getCashRegisterSummary } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Reusable modal for closing a cash register session
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onHide - Callback when modal is closed
 * @param {object} cashRegister - Current cash register data
 * @param {object} summary - Cash register summary (optional, will fetch if not provided)
 * @param {function} onSuccess - Optional callback when cash register is closed successfully
 */
const CloseCashRegisterModal = ({ show, onHide, cashRegister, summary: propSummary, onSuccess }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const prevShowRef = useRef(false);

    const [step, setStep] = useState('input'); // 'input' | 'review'
    const [actualCash, setActualCash] = useState(0);
    const [closeNotes, setCloseNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [summary, setSummary] = useState(propSummary);
    const [loading, setLoading] = useState(false);

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    // Fetch summary on modal OPEN (not on every re-render)
    useEffect(() => {
        const justOpened = show && !prevShowRef.current;
        prevShowRef.current = show;

        if (justOpened) {
            // Reset state only when modal opens
            setStep('input');
            setActualCash(0);
            setCloseNotes('');

            if (!propSummary) {
                const fetchSummary = async () => {
                    try {
                        setLoading(true);
                        const response = await getCashRegisterSummary();
                        setSummary(response.data);
                    } catch (error) {
                        console.error('Error fetching summary:', error);
                        toast.error(t('cashRegisterPage.toasts.fetchFailed'));
                    } finally {
                        setLoading(false);
                    }
                };
                fetchSummary();
            } else {
                setSummary(propSummary);
            }
        }
    }, [show, propSummary, t]);

    const handleClose = () => {
        if (!submitting) {
            setStep('input');
            setActualCash(0);
            setCloseNotes('');
            onHide();
        }
    };

    const handleNextStep = () => {
        setStep('review');
    };

    const handleBack = () => {
        setStep('input');
        setCloseNotes(''); // Clear notes if going back
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (step === 'input') {
            handleNextStep();
            return;
        }

        // Validation for review step
        const difference = summary ? actualCash - summary.totals.expectedCash : 0;
        if (difference !== 0 && !closeNotes.trim()) {
            toast.error(t('cashRegisterPage.closeModal.notesRequired'));
            return;
        }

        setSubmitting(true);
        try {
            await closeCashRegister({
                actual_cash: actualCash,
                notes: closeNotes
            });
            trackAction('cash_register_closed');
            toast.success(t('cashRegisterPage.toasts.closed'));
            handleClose();

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error closing cash register:', error);
            trackAction('cash_register_close_failed');
            toast.error(t('cashRegisterPage.toasts.closeFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            size="lg"
            backdrop={isMobile ? 'static' : true}
        >
            <Modal.Header closeButton>
                <Modal.Title>{t('cashRegisterPage.closeModal.title')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <>
                            {/* STEP 1: BLIND INPUT */}
                            {step === 'input' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('cashRegisterPage.closeModal.actualAmountLabel')}</Form.Label>
                                    <CurrencyInput
                                        value={actualCash}
                                        onChange={(value) => setActualCash(value || 0)}
                                        placeholder="0"
                                        required
                                        autoFocus
                                    />
                                    <Form.Text className="text-muted">
                                        {t('cashRegisterPage.closeModal.blindInputHelp', 'Ingrese el efectivo total en caja sin contar base inicial.')}
                                    </Form.Text>
                                </Form.Group>
                            )}

                            {/* STEP 2: REVIEW & SUMMARY */}
                            {step === 'review' && summary && (
                                <>
                                    <Card className="mb-4 bg-light border-0">
                                        <Card.Body>
                                            <h6 className="mb-3 border-bottom pb-2">{t('cashRegisterPage.closeModal.summary')}</h6>

                                            {/* Opening Amount Row */}
                                            <Row className="mb-3">
                                                <Col xs={6}><strong>{t('cashRegisterPage.openingAmount')}:</strong></Col>
                                                <Col xs={6} className="text-end">{formatCurrency(cashRegister?.opening_amount)}</Col>
                                            </Row>

                                            {/* Table Header - abbreviated for mobile */}
                                            <Row className="mb-2 small fw-bold text-muted border-bottom pb-2">
                                                <Col xs={3}></Col>
                                                <Col xs={3} className="text-end">
                                                    <span className="d-none d-md-inline">{t('cashRegisterPage.closeModal.incomeSection', 'Ingresos')}</span>
                                                    <span className="d-md-none">In</span>
                                                </Col>
                                                <Col xs={3} className="text-end">
                                                    <span className="d-none d-md-inline">{t('cashRegisterPage.closeModal.expenseSection', 'Egresos')}</span>
                                                    <span className="d-md-none">Eg</span>
                                                </Col>
                                                <Col xs={3} className="text-end">Total</Col>
                                            </Row>

                                            {/* Payment Methods Table */}
                                            {summary.summary && Object.entries(summary.summary).map(([key, value]) => (
                                                <Row className="mb-1 small" key={key}>
                                                    <Col xs={3} className="text-muted text-truncate">{t(`cashRegisterPage.methods.${key}`)}</Col>
                                                    <Col xs={3} className="text-end text-success">{value.in > 0 ? formatCurrency(value.in) : '-'}</Col>
                                                    <Col xs={3} className="text-end text-danger">{value.out > 0 ? formatCurrency(value.out) : '-'}</Col>
                                                    <Col xs={3} className="text-end fw-bold">{formatCurrency(value.in - value.out)}</Col>
                                                </Row>
                                            ))}

                                            <hr />
                                            <Row className="mb-2">
                                                <Col xs={6}><strong>{t('cashRegisterPage.expectedCash')}:</strong></Col>
                                                <Col xs={6} className="text-end fw-bold">{formatCurrency(summary.totals.expectedCash)}</Col>
                                            </Row>
                                            <Row className="mb-0 small text-muted ps-2">
                                                <Col xs={7}>{t('cashRegisterPage.openingAmount')}:</Col>
                                                <Col xs={5} className="text-end text-muted">{formatCurrency(cashRegister?.opening_amount)}</Col>
                                            </Row>
                                            <Row className="mb-0 small text-muted ps-2">
                                                <Col xs={7}>+ {t('cashRegisterPage.closeModal.cashInLabel', 'Ingresos efectivo')}:</Col>
                                                <Col xs={5} className="text-end text-success">{formatCurrency(summary.summary?.cash?.in)}</Col>
                                            </Row>
                                            <Row className="mb-3 small text-muted ps-2">
                                                <Col xs={7}>− {t('cashRegisterPage.closeModal.cashOutLabel', 'Egresos efectivo')}:</Col>
                                                <Col xs={5} className="text-end text-danger">{formatCurrency(summary.summary?.cash?.out)}</Col>
                                            </Row>
                                            <Row className="mb-2">
                                                <Col xs={6}><strong>{t('cashRegisterPage.actualCash')}:</strong></Col>
                                                <Col xs={6} className="text-end">{formatCurrency(actualCash)}</Col>
                                            </Row>
                                            <Row className={actualCash - summary.totals.expectedCash !== 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                                <Col xs={6}>{t('cashRegisterPage.difference')}:</Col>
                                                <Col xs={6} className="text-end">{formatCurrency(actualCash - summary.totals.expectedCash)}</Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {(actualCash - summary.totals.expectedCash !== 0) && (
                                        <Alert variant="warning" className="d-flex align-items-center">
                                            <span className="me-2">⚠️</span>
                                            <div>
                                                <strong>{t('cashRegisterPage.closeModal.discrepancyAlert')}</strong>
                                                <div className="small">{t('cashRegisterPage.closeModal.discrepancyMessage')}</div>
                                            </div>
                                        </Alert>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('cashRegisterPage.closeModal.notesLabel')}
                                            {(actualCash - summary.totals.expectedCash !== 0) && <span className="text-danger">*</span>}
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={closeNotes}
                                            onChange={(e) => setCloseNotes(e.target.value)}
                                            required={actualCash - summary.totals.expectedCash !== 0}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={step === 'input' ? handleClose : handleBack} disabled={submitting}>
                        {step === 'input' ? t('cancel') : t('back', 'Atrás')}
                    </Button>
                    <Button variant="dark" type="submit" disabled={submitting || loading}>
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            step === 'input' ? t('cashRegisterPage.closeModal.verifyButton', 'Cerrar Caja') : t('cashRegisterPage.closeModal.confirmButton')
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CloseCashRegisterModal;
